import type {
  AutopilotAttentionCause,
  AutopilotCycleSummary,
  AutopilotStatus,
} from "@auto-pools/core";
import { formatWhen } from "../lib/format";
import { useAutopilot } from "./autopilotContext";
import { useViewer } from "../auth/authContext";
import { rulesChangedSinceStart, useAutopilotResume } from "./useAutopilotResume";

/**
 * The cycle the attention reason is actually about: matched by the reason's own `positionId` when
 * given, else (an older single-position run) the sole held cycle — mirrors the same resolution the
 * Telegram panel uses (apps/server/src/bot/panel/render.ts, `attention()`).
 */
function attentionCycle(
  status: AutopilotStatus,
  positionId: string | null | undefined,
): AutopilotCycleSummary | null {
  const { cycles } = status;
  return (
    (positionId ? cycles.find((c) => c.positionId === positionId) : undefined) ??
    (cycles.length === 1 ? cycles[0] : null) ??
    null
  );
}

function attentionText(
  cause: AutopilotAttentionCause,
  cycle: AutopilotCycleSummary | null,
): string {
  switch (cause) {
    case "close-failing": {
      const closing = cycle?.closing;
      const attempts = closing?.closeAttempts ?? 0;
      const next = closing?.nextAttemptAt
        ? ` · next attempt ${formatWhen(closing.nextAttemptAt)}`
        : "";
      return `A close did not go through ${String(attempts)} times and keeps retrying${next}.`;
    }
    case "outcome-unknown":
      return "An operation's outcome has not been proved for more than 10 minutes.";
    case "opening-unproven":
      return "A failed opening could not be proved empty yet. The autopilot keeps rechecking.";
    case "authority-lost":
      return "Password, wallet or rules changed since Start. Start the autopilot again to resume.";
    case "unfunded-decisions":
      return "Three decisions in a row ended without a funded position.";
    case "claims-remain":
      return "The close needs you: it finished withdraw-only, or a reward remains unclaimed.";
    case "action-failing":
      return "An autopilot action was refused 3 times in a row and keeps retrying; the journal shows why.";
  }
}

interface BannerModel {
  tone: "bad" | "warn";
  heading: string;
  text: string;
  action: { label: string; onClick: () => void; pending?: boolean };
}

export function AutopilotBanner({
  onOpenPosition,
  onOpenAutopilot,
  onOpenSettings,
}: {
  onOpenPosition: (target: { operationId?: string; positionId?: string }) => void;
  onOpenAutopilot: () => void;
  onOpenSettings: () => void;
}) {
  const autopilot = useAutopilot();
  const viewer = useViewer();
  const { resume, resuming, error: resumeError } = useAutopilotResume();
  const view = autopilot.data;
  const status = view?.status ?? null;

  if (!view || !status || status.state === "off") return null;

  let model: BannerModel | null = null;
  if (status.hold) {
    model = {
      tone: "warn",
      heading: "Autopilot on hold.",
      text:
        status.hold === "vault-locked"
          ? "The signing wallet is locked."
          : "The executor is recovering.",
      action: { label: "Open Settings", onClick: onOpenSettings },
    };
  } else if (status.state === "needs-attention" && status.reason?.state === "needs-attention") {
    const { cause, operationId, positionId } = status.reason;
    model = {
      tone: "bad",
      heading: "Autopilot needs attention.",
      text: attentionText(cause, attentionCycle(status, positionId)),
      action: operationId
        ? {
            label: "Open operation",
            onClick: () => {
              onOpenPosition({ operationId, positionId: positionId ?? undefined });
            },
          }
        : positionId
          ? {
              label: "Open position",
              onClick: () => {
                onOpenPosition({ positionId });
              },
            }
          : { label: "Open Autopilot", onClick: onOpenAutopilot },
    };
  } else if (
    status.state === "limit" &&
    status.reason?.state === "limit" &&
    status.reason.limit === "drawdown"
  ) {
    model = rulesChangedSinceStart(view.rules)
      ? {
          tone: "warn",
          heading: "Autopilot stopped.",
          text: "The drawdown limit was hit and the strategy rules changed since Start; pause it and start again.",
          action: { label: "Open Autopilot", onClick: onOpenAutopilot },
        }
      : {
          tone: "warn",
          heading: "Autopilot stopped.",
          text: "The drawdown limit was hit; new entries wait for Resume.",
          // Resume is a command: a viewer is pointed at the Autopilot page instead.
          action: viewer
            ? { label: "Open Autopilot", onClick: onOpenAutopilot }
            : {
                label: resuming ? "Resuming…" : "Resume now",
                onClick: () => void resume(),
                pending: resuming,
              },
        };
  }
  if (!model) return null;

  return (
    <div role="alert" className={`status-banner autopilot-banner ${model.tone}`}>
      <svg aria-hidden="true" viewBox="0 0 16 16">
        <circle cx="8" cy="8" r="6.5" />
        <path d="M8 4.8v3.6M8 11v.1" />
      </svg>
      <span>
        <strong>{model.heading}</strong> {model.text}
        {resumeError && <span className="autopilot-banner-error"> {resumeError}</span>}
      </span>
      <button type="button" disabled={model.action.pending} onClick={model.action.onClick}>
        {model.action.label}
      </button>
    </div>
  );
}
